<template>
  <v-dialog :value="true" max-width="960" persistent scrollable>
    <v-card>
      <v-card-title class="headline">
        <h2 class="outfit-regular">{{ dialogTitle }}</h2>
        <v-spacer></v-spacer>
        <v-btn v-if="currentStep !== 'customUsername'" icon @click="goToStep('customUsername')" title="Create Custom Username">
          <v-icon>mdi-account-plus-outline</v-icon>
        </v-btn>
        <v-btn v-else icon @click="goToStep('selectRealm')" title="Select from Predefined Avatars/Realms">
          <v-icon>mdi-earth</v-icon>
        </v-btn>
      </v-card-title>
      <v-divider></v-divider>

      <v-card-text class="pt-5 min-height-400">
        <!-- Step 1: Select Realm -->
        <div v-if="currentStep === 'selectRealm'">
          <p class="subtitle-1">Choose a Realm to explore its Avatars, or create a custom username.</p>
          <v-progress-linear indeterminate v-if="loadingRealms"></v-progress-linear>

          <v-text-field v-model="searchTerm" label="Search Realms & Characters" outlined dense></v-text-field>

          <div v-if="!loadingRealms && filteredRealms.length">
            <v-carousel v-if="!searchTerm" v-model="model" hide-delimiters>
              <v-carousel-item v-for="(realm, i) in filteredRealms" :key="realm.id" :src="realm.landscape?.image" @click="selectRealmForCharacterViewing(realm)">
                <div class="d-flex flex-column width-100 height-100 justify-end">
                  <div style="background: linear-gradient(to right, rgba(0,0,0,0.5), transparent);" class="d-flex flex-column width-100">
                    <div class="outfit-regular font-size-24 pa-4 white--text">{{ realm.name }}</div>
                    <p class="text-caption white--text px-4">{{ realm.concept ? realm.concept.substring(0, 140) + '...' : 'No concept.' }}</p>
                  </div>
                </div>
              </v-carousel-item>
            </v-carousel>

            <!-- Show searched characters when search is active -->
            <div v-if="searchTerm && searchedCharacters.length" class="mt-4">
              <h3 class="subtitle-1 mb-2">Matching Characters</h3>
              <v-progress-linear v-if="loadingSearchedCharacters" indeterminate></v-progress-linear>
              <v-row>
                <v-col v-for="character in searchedCharacters" :key="character.id" cols="6" sm="4" md="3">
                  <v-card hover @click="processPredefinedCharacterSelection(character)" class="text-center pa-2">
                    <v-avatar size="80" class="mb-2 elevation-2">
                      <v-img :src="character.avatar || ''"></v-img>
                    </v-avatar>
                    <div class="body-2 font-weight-bold">{{ character.name }}</div>
                    <div v-if="character.realmId" class="caption grey--text">From {{ getRealmName(character.realmId) }}</div>
                  </v-card>
                </v-col>
              </v-row>
            </div>

            <!-- Show grid view of realms when not searching -->
            <div v-if="searchTerm">
              <h3 class="subtitle-1 mt-3 mb-2">Explore Realms</h3>
              <v-row>
                <v-col v-for="realm in filteredRealms" :key="realm.id" cols="12" md="6" lg="4">
                  <v-card hover @click="selectRealmForCharacterViewing(realm)" class="d-flex flex-column" height="100%">
                    <v-img :src="realm.landscape?.image || ''" height="150px" class="white--text align-end" gradient="to bottom, rgba(0,0,0,.1), rgba(0,0,0,.7)">
                      <v-card-title class="text-subtitle-1 py-1">{{ realm.name }}</v-card-title>
                    </v-img>
                    <v-card-text class="flex-grow-1"><p class="text-caption mb-0">{{ realm.concept ? realm.concept.substring(0, 100) + '...' : 'No concept.' }}</p></v-card-text>
                    <v-card-actions class="justify-center">
                      <v-btn small color="primary" text @click.stop="selectRealmForCharacterViewing(realm)">View Avatars</v-btn>
                    </v-card-actions>
                  </v-card>
                </v-col>
              </v-row>
            </div>
          </div>
          <v-alert v-if="!loadingRealms && !filteredRealms.length" type="info" dense class="mt-4">
            No realms found to display. You can create a custom username.
          </v-alert>
        </div>

        <!-- Step 2: Show Available Characters in Selected Realm -->
        <div v-if="currentStep === 'selectCharacter' && selectedRealm">
          <v-btn text small @click="goToStep('selectRealm')" class="mb-3">
            <v-icon left>mdi-arrow-left</v-icon> Back to Realms
          </v-btn>
          <p class="subtitle-1">Avatars available in <strong>{{ selectedRealm.name }}</strong>:</p>
          <v-progress-linear indeterminate v-if="loadingCharacters"></v-progress-linear>
           <v-alert v-if="!loadingCharacters && !filteredCharacters.length" type="info">
             No predefined avatars currently available for this realm.
           </v-alert>
           <v-row v-if="!loadingCharacters && filteredCharacters.length">
             <v-col v-for="character in filteredCharacters" :key="character.id" cols="6" sm="4" md="3">
                <v-card hover @click="processPredefinedCharacterSelection(character)" class="text-center pa-2">
                  <v-avatar size="80" class="mb-2 elevation-2"><v-img :src="character.avatar || ''"></v-img></v-avatar>
                  <div class="body-2 font-weight-bold">{{ character.name }}</div>
                </v-card>
              </v-col>
           </v-row>
        </div>

        <!-- Step 3: Custom Username Creation -->
        <div v-if="currentStep === 'customUsername'">
          <p>Create your unique username with two words separated by a space.</p>
          <v-row>
            <v-col cols="12">
              <v-text-field 
                v-model="customUsername" 
                label="Enter your username (letters only, one space)" 
                outlined 
                @keydown="processUsernameInput"
                @keydown.enter="processCustomUsernameSelection"
                :rules="[
                  v => !!v || 'Username is required',
                  v => /^[a-zA-Z]+ [a-zA-Z]+$/.test(v) || 'Must be two words with only letters separated by one space'
                ]"
              ></v-text-field>
            </v-col>
          </v-row>
          <p v-if="customUsername && /^[a-zA-Z]+ [a-zA-Z]+$/.test(customUsername)" class="title my-3">
            Proposed Username: <strong class="deep-purple--text">{{ customUsername }}</strong>
          </p>
          <v-alert v-if="usernameStatusMessage" :type="usernameStatusType" dense text>{{ usernameStatusMessage }}</v-alert>
          <v-btn color="primary" @click="processCustomUsernameSelection" :loading="settingUsername" :disabled="!customUsername || !/^[a-zA-Z]+ [a-zA-Z]+$/.test(customUsername)">
            Check & Claim Username
          </v-btn>
        </div>
      </v-card-text>

      <v-overlay :value="isOverallLoading">
        <v-progress-circular indeterminate size="64"></v-progress-circular>
        <p class="mt-3 white--text">{{ overlayText }}</p>
      </v-overlay>

      <!-- Confirmation Dialog -->
      <v-dialog v-model="confirmDialog.show" max-width="400">
        <v-card>
          <v-card-title>{{ confirmDialog.title }}</v-card-title>
          <v-card-text>{{ confirmDialog.text }}</v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="confirmDialog.show = false">Cancel</v-btn>
            <v-btn 
              color="primary" 
              @click="confirmDialogConfirm()"
              :loading="confirmDialog.loading"
            >
              Confirm
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

    </v-card>
  </v-dialog>
</template>

<script>
import { db } from '@/firebase/init';
import { doc, getDoc, collection, query, where, getDocs, updateDoc, writeBatch, serverTimestamp, limit } from 'firebase/firestore'; // Removed unused orderBy, startAt, endAt for now
import { getAuth } from 'firebase/auth';

export default {
  name: 'CharacterSelector',
  // No props needed for dialog visibility if parent controls via v-if
  data() {
    return {
      // displayCharacterSelector: true, // This was in your data, but v-dialog v-model should be 'dialog'
      dialog: true, // If this component IS the dialog, it should manage its own v-model
                    // If parent wraps this in a <v-dialog>, then this 'dialog' prop is not needed here.
                    // For consistency with your first example of this component, let's assume it IS the dialog.
      currentStep: 'selectRealm',
      model: 0, // For carousel v-model

      realms: [],
      loadingRealms: false,
      selectedRealm: null,

      availableCharacters: [],
      loadingCharacters: false,

      customUsername: '',
      usernameStatusMessage: '',
      usernameStatusType: 'info',

      settingUsername: false,
      claimingCharacter: false,

      // Search fields are commented out, matching your template's current state
      searchTerm: '',
      searchedCharacters: [],
      loadingSearchedCharacters: false,

      // For UI notifications
      // snackbar: {
      //   show: false,
      //   text: '',
      //   color: 'success',
      //   timeout: 3000
      // },
      confirmDialog: {
        show: false,
        title: '',
        text: '',
        callback: null,
        data: null,
        loading: false
      },
    };
  },
  computed: {
    authUser() {
      return getAuth().currentUser;
    },
    filteredRealms() {
      if (!this.searchTerm.trim()) return this.realms;
      const term = this.searchTerm.toLowerCase().trim();
      // Ensure we're returning the full realm objects with their IDs intact
      return this.realms.filter(realm => 
        (realm.name?.toLowerCase().includes(term) || 
        realm.concept?.toLowerCase().includes(term))
      );
    },
    filteredCharacters() {
      if (!this.searchTerm.trim() || !this.selectedRealm) return this.availableCharacters;
      const term = this.searchTerm.toLowerCase().trim();
      return this.availableCharacters.filter(character => 
        character.name?.toLowerCase().includes(term) || 
        character.description?.toLowerCase().includes(term)
      );
    },
    dialogTitle() {
      if (this.currentStep === 'selectRealm') return 'Choose Your Username';
      if (this.currentStep === 'selectCharacter') return `Avatars in ${this.selectedRealm?.name || 'Realm'}`;
      if (this.currentStep === 'customUsername') return 'Create Your Custom Username';
      return 'Choose Your Avatar';
    },
    isOverallLoading() {
      return this.loadingRealms || this.loadingCharacters || this.settingUsername || this.claimingCharacter;
    },
    overlayText() {
      if (this.loadingRealms) return 'Loading Realms...';
      if (this.loadingCharacters) return 'Loading Avatars...';
      if (this.settingUsername) return 'Processing Username...';
      if (this.claimingCharacter) return 'Claiming Avatar...';
      return '';
    }
  },
  
  mounted() {
    this.currentStep = 'selectRealm'; // Default step
    if (!this.realms.length) {
      this.fetchRealms();
    }
  },
  methods: {
    goToStep(step) {
      this.currentStep = step;
      if (step === 'selectRealm') {
        this.selectedRealm = null;
        this.availableCharacters = [];
      }
      if (step === 'customUsername') {
        this.customUsername = '';
        this.usernameStatusMessage = '';
      }
    },
    async fetchRealms() {
      this.loadingRealms = true;
      this.realms = [];
      try {
        const realmsRef = collection(db, 'realms');
        const q = query(realmsRef, limit(20));
        const querySnapshot = await getDocs(q);
        let realms = [];
        querySnapshot.docs.forEach(doc => {
          console.log("Realm document:", doc.id);
          let realm = doc.data();
          realm.id = doc.id;
          realms.push(realm);
        });
        this.realms = realms;
        console.log("Realms with IDs:", this.realms.map(r => ({ id: r.id, name: r.name })));
      } catch (error) {
        console.error("Error fetching realms:", error);
      } finally {
        this.loadingRealms = false;
      }
    },
    async selectRealmForCharacterViewing(realm) {
      console.log("Selecting realm:", realm);
      console.log("Realm ID:", realm.id);
      this.selectedRealm = realm;
      this.currentStep = 'selectCharacter';
      this.availableCharacters = [];
      this.loadingCharacters = true;
      try {
        const charactersRef = collection(db, 'characters');
        const q = query(
          charactersRef,
          where("realmId", "==", realm.id),
          where("status", "==", "available"),
          limit(24)
        );
        const querySnapshot = await getDocs(q);
        this.availableCharacters = querySnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      } catch (error) {
        console.error("Error fetching characters for realm:", error);
        this.availableCharacters = [];
      }
      this.loadingCharacters = false;
    },

    async processPredefinedCharacterSelection(character) {
      if (!this.authUser || !this.authUser.uid) {
        this.showSnackbar("Authentication error. Please log in again.", "error");
        return;
      }
      
      const userProfileDocId = this.profile?.id || this.authUser.uid;
      if (!userProfileDocId) {
        this.showSnackbar("User profile identifier is missing. Cannot update profile.", "error");
        return;
      }

      this.showConfirmDialog(
        "Confirm Username",
        `Choose "${character.name}" as your username?`,
        async () => {
          this.confirmDialog.loading = true;
          
          try {
            const batch = writeBatch(db);
            const globalCharDocRef = doc(db, 'characters', character.id);
            batch.update(globalCharDocRef, { 
              status: 'taken', 
              userId: this.authUser.uid, 
              updatedAt: serverTimestamp() 
            });

            const userProfileDocRef = doc(db, 'users', userProfileDocId);
            batch.update(userProfileDocRef, { 
              username: character.name, 
              characterId: character.id, 
              avatar: character.avatar, 
              realm: {
                id: this.selectedRealm.id,
                name: this.selectedRealm.name,
                landscape: this.selectedRealm.landscape?.image || ''
              },
              updatedAt: serverTimestamp() 
            });

            await batch.commit();
            this.showSnackbar(`Username "${character.name}" claimed!`, "success");
          } catch (error) {
            console.error("Error claiming character:", error);
            this.showSnackbar("Failed to claim character. It might have been taken.", "error");
            if (this.selectedRealm) { 
              await this.selectRealmForCharacterViewing(this.selectedRealm); 
            }
          } finally {
            this.confirmDialog.loading = false;
            this.confirmDialog.show = false;
          }
        }
      );
    },

    async processCustomUsernameSelection() {
      this.usernameStatusMessage = '';
      if (!this.customUsername || !/^[a-zA-Z]+ [a-zA-Z]+$/.test(this.customUsername)) {
        this.usernameStatusMessage = "Invalid username format. Must be two words with only letters separated by one space.";
        this.usernameStatusType = "error";
        return;
      }

      if (!this.authUser || !this.authUser.uid) {
        this.showSnackbar("Authentication error. Please log in again.", "error");
        return;
      }
      
      const userProfileDocId = this.profile?.id || this.authUser.uid;
      if (!userProfileDocId) {
        this.showSnackbar("User profile identifier is missing. Cannot update profile.", "error");
        return;
      }

      this.settingUsername = true;
      const chosenUsername = this.customUsername;
      const normalizedId = chosenUsername.toLowerCase().replace(/\s+/g, '-');

      try {
        const charDocRef = doc(db, 'characters', normalizedId);
        const docSnap = await getDoc(charDocRef);
        if (docSnap.exists()) {
          this.usernameStatusMessage = `Sorry, "${chosenUsername}" is already taken.`;
          this.usernameStatusType = 'error';
          this.settingUsername = false;
          return;
        }

        const batch = writeBatch(db);
        batch.set(charDocRef, {
          name: chosenUsername,
          normalizedName: chosenUsername.toLowerCase(),
          status: 'taken', 
          type: 'custom_user',
          avatar: '', 
          realmId: null,
          userId: this.authUser.uid,
          createdAt: serverTimestamp(), 
          updatedAt: serverTimestamp(),
        });

        const userProfileDocRef = doc(db, 'users', userProfileDocId);
        batch.update(userProfileDocRef, { 
          username: chosenUsername, 
          characterId: normalizedId, 
          realm: null,
          updatedAt: serverTimestamp() 
        });

        await batch.commit();
        this.showSnackbar(`Username "${chosenUsername}" successfully set!`, "success");
      } catch (error) {
        console.error("Error setting custom username:", error);
        this.usernameStatusMessage = "Failed to set username: " + (error.message || "Unknown error");
        this.usernameStatusType = 'error';
      } finally {
        this.settingUsername = false;
      }
    },

    async searchAllCharacters() {
      if (!this.searchTerm.trim() || this.searchTerm.length < 2) {
        this.searchedCharacters = [];
        return;
      }
      
      this.loadingSearchedCharacters = true;
      try {
        const charactersRef = collection(db, 'characters');
        
        // Remove all filters except status to get a broader range of results
        const q = query(
          charactersRef,
          where("status", "==", "available"),
          limit(300) // Significantly increased limit
        );
        
        const querySnapshot = await getDocs(q);
        console.log("Total characters retrieved:", querySnapshot.docs.length);
        
        // Map with more detailed logging
        const allCharacters = querySnapshot.docs.map(d => {
          const data = d.data();
          // Log a sample to see what fields are available
          if (d === querySnapshot.docs[0]) {
            console.log("Sample character fields:", Object.keys(data));
          }
          return { id: d.id, ...data };
        });
        
        // Use a more permissive filter to catch more results
        const term = this.searchTerm.toLowerCase().trim();
        this.searchedCharacters = allCharacters.filter(character => {
          // Try to match against any string field that might contain the search term
          for (const [key, value] of Object.entries(character)) {
            if (typeof value === 'string' && value.toLowerCase().includes(term)) {
              return true;
            }
          }
          return false;
        });
        
        console.log("Filtered characters count:", this.searchedCharacters.length);
        if (this.searchedCharacters.length > 0) {
          console.log("First matching character:", this.searchedCharacters[0]);
        }
      } catch (error) {
        console.error("Error searching characters:", error);
        this.searchedCharacters = [];
      } finally {
        this.loadingSearchedCharacters = false;
      }
    },
    
    getRealmName(realmId) {
      if (!realmId) return 'Unknown Realm';
      const realm = this.realms.find(r => r.id === realmId);
      return realm ? realm.name : 'Unknown Realm';
    },

    processUsernameInput(event) {
      let key = event.key;
      // if key is not a letter or space, prevent default
      if (!/^[a-zA-Z ]$/.test(key)) {
        key = '';
        return;
      }
      console.log("Event:", event);
      console.log("Key:", key);
      // disable default 
      event.preventDefault();
      event.stopPropagation();

      this.customUsername += key;
    },

    showSnackbar(text, color = 'success', timeout = 3000) {
      this.$snackbarStore.showSnackbar(text, color, timeout);
    },
    
    showConfirmDialog(title, text, callback, data = null) {
      this.confirmDialog.title = title;
      this.confirmDialog.text = text;
      this.confirmDialog.callback = callback;
      this.confirmDialog.data = data;
      this.confirmDialog.loading = false;
      this.confirmDialog.show = true;
    },
    
    confirmDialogConfirm() {
      if (typeof this.confirmDialog.callback === 'function') {
        this.confirmDialog.callback(this.confirmDialog.data);
      }
    },
  },
  watch: {
    searchTerm: {
      handler(newValue) {
        if (this.currentStep === 'selectRealm' && newValue.length >= 2) {
          this.searchAllCharacters();
        }
      },
      debounce: 300
    }
  },
};
</script>

<style scoped>
  .outfit-regular { font-family: 'Outfit', sans-serif; }
  .min-height-400 { min-height: 400px; }
</style>